import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Button,
  Container,
  Group,
  SegmentedControl,
  Skeleton,
  Stack,
  Text,
  TextInput,
  Title,
  UnstyledButton,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import type { ItemCounts, ItemKind, ItemView, SavedItem } from '@nelnela/contracts';
import { api } from '../lib/api';
import {
  bandLabel,
  bands,
  groupByBand,
  isGrouped,
  isVerb,
  keepState,
  rowTrailing,
  scopeLabel,
  stateChips,
  type Scope,
  type StateFilter,
  type Trailing,
} from '../lib/words';
import {
  SWIPE_OPEN_PX,
  savedTitle,
  savedTrack,
  savedTranslation,
  splitSelection,
  swipeAxis,
  swipeOffset,
  swipeSettles,
  type SwipeVerdict,
} from '../lib/saved';
import { speak, ttsCalmLine, ttsPlayer, useTtsKeyStatus } from '../lib/tts-player';
import { useOnline, warmAudio } from '../lib/sw';
import { InfoStrip } from '../components/InfoStrip';
import { IconPause, IconPlay } from '../components/watch/icons';

type Segment = 'vocabulary' | 'patterns' | 'grammar' | 'saved';

/**
 * F7 Words — the inventory, rebuilt to M10-α as a graded dictionary.
 *
 * WHAT CHANGED, and why it is not just more controls. Words used to be a view of what the learner
 * had already met: `scope: 'met'` hardcoded, no way to see the other 5,481 words in the catalog,
 * and a search that only matched Georgian. α's answer is a SCOPE PAIR above the list — "My words ·
 * 214 / Catalog · 5,695" — deliberately NOT a fifth segment, because the segments mean content
 * type and scope is a different question about the same content; "Catalog" exclusive with
 * "Patterns" is not how anyone thinks about a dictionary.
 *
 * The decisions live in `lib/words.ts` and are tested there; this file is wiring.
 *
 * A DEVIATION FROM α, recorded here because the export asks build sessions to record them: α calls
 * for the 5,695-row list to virtualize with react-virtuoso and for an index rail to scrub it.
 * Neither shipped. `react-virtuoso` is not a dependency of this app and the list is PAGED at the
 * API — so the rail would scrub a list the client does not have, and virtualizing 50 rows buys
 * nothing. The band grid does the navigating instead, which is what the rail was for: pick a band,
 * get that band. Revisit if the page size ever grows to the whole catalog.
 */
export function Words() {
  const [seg, setSeg] = useState<Segment>('vocabulary');
  const [scope, setScope] = useState<Scope>('met');
  const online = useOnline();
  const counts = useQuery({ queryKey: ['itemCounts'], queryFn: () => api.itemCounts() });

  // Saved is a personal capture list; there is no catalog of someone else's saves. Rather than
  // disable the segment in one scope or the scope in one segment, the two simply agree: opening
  // Saved is opening My words.
  const setSegment = (v: Segment) => {
    setSeg(v);
    if (v === 'saved') setScope('met');
  };
  const segments =
    scope === 'verified'
      ? [
          { value: 'vocabulary', label: 'Vocabulary' },
          { value: 'patterns', label: 'Patterns' },
          { value: 'grammar', label: 'Grammar' },
        ]
      : [
          { value: 'vocabulary', label: 'Vocabulary' },
          { value: 'patterns', label: 'Patterns' },
          { value: 'grammar', label: 'Grammar' },
          { value: 'saved', label: 'Saved' },
        ];

  return (
    <Container size={640} py="lg">
      <Stack gap="md">
        {!online && (
          <InfoStrip>
            Offline — Today, Words and saved audio work; Watch needs the network.
          </InfoStrip>
        )}
        <Title order={2}>Words</Title>

        {/* α: the scope pair sits ABOVE the segments, with honest counts. */}
        <Group gap={8}>
          {(['met', 'verified'] as const).map((s) => (
            <ScopeChip
              key={s}
              active={scope === s}
              label={scopeLabel(s, counts.data)}
              onClick={() => {
                setScope(s);
                if (s === 'verified' && seg === 'saved') setSeg('vocabulary');
              }}
            />
          ))}
        </Group>

        <SegmentedControl
          fullWidth
          value={seg}
          onChange={(v) => setSegment(v as Segment)}
          data={segments}
        />
        {scope === 'met' && <Proposals />}
        {seg === 'saved' && <SavedList />}
        {seg === 'vocabulary' && (
          <ItemList
            kind="lexeme"
            scope={scope}
            counts={counts.data}
            empty={
              scope === 'verified'
                ? 'No catalog words match that.'
                : 'Words you meet in videos collect here.'
            }
          />
        )}
        {seg === 'patterns' && (
          <ItemList
            kind="pattern"
            scope={scope}
            counts={counts.data}
            empty={
              scope === 'verified'
                ? 'No catalog patterns match that.'
                : 'Patterns you save (or the app notices) collect here.'
            }
          />
        )}
        {seg === 'grammar' && <GrammarIndex />}
      </Stack>
    </Container>
  );
}

/** The scope pair. Custom, not a SegmentedControl — these are chips beside a heading, not a mode. */
function ScopeChip({
  active,
  label,
  onClick,
}: {
  active: boolean;
  label: string;
  onClick: () => void;
}) {
  return (
    <UnstyledButton
      onClick={onClick}
      aria-pressed={active}
      style={{
        fontSize: 12.5,
        padding: '5px 12px',
        borderRadius: 999,
        // α: ember marks the ACTIVE choice here, which is the "active nav" role, not a reward.
        background: active
          ? 'color-mix(in srgb, var(--nn-accent) 13%, transparent)'
          : 'transparent',
        border: `1px solid ${active ? 'color-mix(in srgb, var(--nn-accent) 35%, transparent)' : 'var(--nn-hairline)'}`,
        color: active ? 'var(--nn-accent-text)' : 'var(--nn-muted)',
      }}
    >
      {label}
    </UnstyledButton>
  );
}

/**
 * One inventory segment: search, the scope's state chips, and either the band navigation (catalog)
 * or the sorts that mean something in My words.
 */
function ItemList({
  kind,
  scope,
  counts,
  empty,
}: {
  kind: ItemKind;
  scope: Scope;
  counts: ItemCounts | undefined;
  empty: string;
}) {
  const [state, setState] = useState<StateFilter>('all');
  const [sort, setSort] = useState<'recent' | 'weakest' | 'due'>('recent');
  const [search, setSearch] = useState('');
  const [band, setBand] = useState<number | null>(null);
  const [bandsOpen, setBandsOpen] = useState(false);

  // A filter the new scope does not offer must not survive the switch, or the list goes empty
  // with nothing on screen to explain why.
  useEffect(() => setState((st) => keepState(scope, st)), [scope]);
  useEffect(() => {
    if (scope !== 'verified') setBand(null);
  }, [scope]);

  const grouped = isGrouped(scope, search);
  const q = useInfiniteQuery({
    queryKey: ['items', kind, scope, state, sort, search, band],
    queryFn: ({ pageParam }) =>
      api.itemList({
        kind,
        scope,
        // The catalog browses in level order — the only order a band header can head. My words
        // keeps the sorts that mean something about a learner's own schedule.
        sort: scope === 'verified' ? 'level' : sort,
        ...(state === 'all' ? {} : { state }),
        ...(search ? { q: search } : {}),
        ...(band !== null ? { levelMin: band, levelMax: band + 9 } : {}),
        // A band holds a few hundred words, so one page is the whole band; without a filter the
        // catalog pages like anything else.
        ...(band !== null ? { limit: 200 } : {}),
        ...(pageParam ? { cursor: pageParam } : {}),
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const items = q.data?.pages.flatMap((p) => p.items) ?? [];
  const groups = groupByBand(items, grouped);
  const now = Date.now();

  return (
    <Stack gap="xs">
      {/* α: search is a real field now — the dictionary's front door — and it says in the
          placeholder that it takes English, because that is the half nobody expects to work. */}
      <TextInput
        size="sm"
        placeholder={scope === 'verified' ? 'Search Georgian or English — try “water”' : 'Search'}
        value={search}
        onChange={(e) => setSearch(e.currentTarget.value)}
        aria-label="Search words"
      />
      <Group gap={8} wrap="wrap">
        <SegmentedControl
          size="xs"
          value={state}
          onChange={(v) => setState(v as StateFilter)}
          data={stateChips(scope).map((c) => ({
            value: c,
            label: c === 'all' ? 'all' : c === 'unmet' ? 'not met' : c === 'shaky' ? '◐' : '●',
          }))}
          aria-label="Filter by state"
        />
        {scope === 'met' ? (
          <SegmentedControl
            size="xs"
            value={sort}
            onChange={(v) => setSort(v as 'recent' | 'weakest' | 'due')}
            data={[
              { value: 'due', label: 'due soonest' },
              { value: 'recent', label: 'recent' },
              { value: 'weakest', label: 'weakest' },
            ]}
            aria-label="Sort"
          />
        ) : (
          <UnstyledButton
            onClick={() => setBandsOpen((o) => !o)}
            style={{
              fontSize: 12,
              padding: '5px 11px',
              borderRadius: 8,
              border: '1px solid var(--nn-hairline)',
              color: band === null ? 'var(--nn-muted)' : 'var(--nn-accent-text)',
            }}
          >
            {band === null ? 'all bands ▾' : `${bandLabel(band)} ▾`}
          </UnstyledButton>
        )}
      </Group>

      {bandsOpen && scope === 'verified' && (
        <BandPanel
          counts={counts}
          value={band}
          onPick={(level) => {
            setBand(level);
            setBandsOpen(false);
          }}
        />
      )}

      {q.isPending ? (
        <>
          <Skeleton height={44} radius={10} />
          <Skeleton height={44} radius={10} />
          <Skeleton height={44} radius={10} />
        </>
      ) : items.length === 0 ? (
        <div
          style={{
            background: 'var(--nn-panel)',
            border: '1px solid var(--nn-hairline)',
            borderRadius: 12,
            padding: 16,
          }}
        >
          <Text fz={13} c="dimmed" style={{ lineHeight: 1.55 }}>
            {empty}
          </Text>
        </div>
      ) : (
        groups.map((g, gi) => (
          <div key={g.level ?? `g${gi}`}>
            {g.level !== null && grouped && <BandHeader level={g.level} counts={counts} />}
            <Stack gap="xs">
              {g.items.map((v) => (
                <DictionaryRow
                  key={v.item.id}
                  view={v}
                  trailing={rowTrailing(v, scope, grouped, now)}
                />
              ))}
            </Stack>
          </div>
        ))
      )}
      {q.hasNextPage && (
        <Button
          variant="default"
          fullWidth
          onClick={() => q.fetchNextPage()}
          loading={q.isFetchingNextPage}
        >
          Load more
        </Button>
      )}
    </Stack>
  );
}

/**
 * α: "band headers in the list: custom sticky DOM, 10px mono --nn-faint". Neither accent — band
 * headers and counts are on the theme's `neither` list, and for the reason the count itself gives:
 * coverage is a fact, not a score, so it gets no colour that could read as praise or warning.
 */
function BandHeader({ level, counts }: { level: number; counts: ItemCounts | undefined }) {
  const b = bands(counts).find((x) => x.level === level);
  return (
    <div
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 2,
        background: 'var(--nn-wash)',
        padding: '10px 2px 6px',
        fontFamily: 'ui-monospace, Menlo, Consolas, monospace',
        fontSize: 10,
        letterSpacing: '.08em',
        color: 'var(--nn-faint)',
        display: 'flex',
        justifyContent: 'space-between',
        gap: 10,
      }}
    >
      <span>{bandLabel(level)}</span>
      {b && <span>{b.detail}</span>}
    </div>
  );
}

/**
 * The band grid — α's navigation, as an INLINE panel rather than the bottom Drawer it drew.
 *
 * A recorded deviation. Built first as a Mantine `Drawer` in the shape every other sheet in this
 * app uses (`VocabSheet`, `ScenarioSheet`): `position="bottom"`, `size="auto"`, its own painted
 * surface. It came out translucent, with the word list legible straight through the grid, and
 * then anchored to the top of the viewport instead of the bottom. Two rounds of matching the house
 * pattern more exactly did not move it.
 *
 * Rather than keep spending on a portal's internals, the grid expands in place under the chip that
 * opens it. It navigates identically — pick a band, get that band — needs no portal, no z-index
 * against the tab bar and no overlay, and it is the same control on desktop, where α wanted a band
 * PANEL rather than a sheet anyway. The bottom sheet is worth revisiting if the Drawer is ever
 * understood; the navigation is not waiting for it.
 */
function BandPanel({
  counts,
  value,
  onPick,
}: {
  counts: ItemCounts | undefined;
  value: number | null;
  onPick: (level: number | null) => void;
}) {
  return (
    <div
      style={{
        background: 'var(--nn-panel)',
        border: '1px solid var(--nn-hairline)',
        borderRadius: 12,
        padding: 12,
      }}
    >
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(96px, 1fr))',
          gap: 8,
        }}
      >
        {bands(counts).map((b) => (
          <UnstyledButton
            key={b.level}
            onClick={() => onPick(b.level)}
            style={{
              padding: '9px 10px',
              borderRadius: 10,
              textAlign: 'left',
              background:
                value === b.level
                  ? 'color-mix(in srgb, var(--nn-accent) 13%, transparent)'
                  : 'transparent',
              border: `1px solid ${value === b.level ? 'color-mix(in srgb, var(--nn-accent) 35%, transparent)' : 'var(--nn-hairline)'}`,
              color: value === b.level ? 'var(--nn-accent-text)' : 'var(--nn-text)',
            }}
          >
            <div style={{ fontSize: 12.5 }}>{`${b.level}–${b.level + 9}`}</div>
            <div
              style={{
                fontSize: 10,
                fontFamily: 'ui-monospace, Menlo, Consolas, monospace',
                color: 'var(--nn-faint)',
              }}
            >
              {b.words.toLocaleString('en-US')}
            </div>
          </UnstyledButton>
        ))}
      </div>
      {value !== null && (
        <Button variant="default" fullWidth mt="sm" onClick={() => onPick(null)}>
          show all bands
        </Button>
      )}
    </div>
  );
}

/**
 * The dictionary row: the word, its gloss, the ვ‑ chip for a verb, and whatever the scope puts on
 * the right (`rowTrailing`).
 *
 * The gloss is the change that makes this a dictionary rather than an inventory — a list of
 * Georgian words with no meanings beside them is only useful to someone who already knows them.
 */
function DictionaryRow({ view, trailing }: { view: ItemView; trailing: Trailing }) {
  const gloss = view.catalog?.gloss ?? null;
  return (
    <Link
      to={`/words/item/${view.item.id}`}
      style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}
    >
      <Group
        justify="space-between"
        wrap="nowrap"
        gap={10}
        style={{
          background: 'var(--nn-panel)',
          border: '1px solid var(--nn-hairline)',
          borderRadius: 10,
          padding: '10px 13px',
        }}
      >
        <div style={{ minWidth: 0 }}>
          <Group gap={8} wrap="nowrap">
            <Text fz={14.5} lang="ka" truncate>
              {view.item.displayForm ?? view.item.lemma}
            </Text>
            {isVerb(view) && <VerbChip />}
          </Group>
          {gloss && (
            <Text fz={12} c="dimmed" truncate>
              {gloss}
            </Text>
          )}
        </div>
        {trailing.kind !== 'none' && (
          <Text fz={11} c="dimmed" style={{ flexShrink: 0 }}>
            {trailing.text}
          </Text>
        )}
      </Group>
    </Link>
  );
}

/** α: the ვ‑ chip marks a verb, and rides SKY — the theme's role for "forms mark (ვ‑)". */
function VerbChip() {
  return (
    <span
      style={{
        flexShrink: 0,
        fontSize: 10,
        lineHeight: 1,
        padding: '3px 5px',
        borderRadius: 5,
        background: 'color-mix(in srgb, var(--nn-info) 15%, transparent)',
        color: 'var(--nn-info-text)',
      }}
      aria-label="verb — has a forms table"
    >
      ვ‑
    </span>
  );
}

/** F14: the browsable grammar index, by level, marked with what the learner has met. */
function GrammarIndex() {
  const q = useQuery({ queryKey: ['lessons'], queryFn: () => api.lessonIndex() });
  if (q.isPending) return <Skeleton height={120} radius={12} />;
  const rows = q.data ?? [];
  if (rows.length === 0) {
    return (
      <Text c="dimmed" fz="sm">
        The grammar index fills up as lessons are written.
      </Text>
    );
  }
  return (
    <Stack gap="xs">
      {rows.map((r) => (
        <Link
          key={r.lesson.id}
          to={`/words/lesson/${r.lesson.id}`}
          style={{ textDecoration: 'none', color: 'inherit' }}
        >
          <div
            style={{
              background: 'var(--nn-panel)',
              border: '1px solid var(--nn-hairline)',
              borderRadius: 10,
              padding: '11px 13px',
            }}
          >
            <Group justify="space-between" wrap="nowrap" gap={10}>
              <Text fz={14}>{r.lesson.title}</Text>
              <Text fz={11} c="dimmed">
                {r.state === 'solid' ? '●' : r.state === 'met' ? '◐' : '○'}
              </Text>
            </Group>
            <Text fz={12} c="dimmed" mt={2} lineClamp={2}>
              {r.lesson.ruleSummary}
            </Text>
          </div>
        </Link>
      ))}
    </Stack>
  );
}

/** The miner's output: "N patterns I've noticed — review?" → Track / Not now (F7). */
function Proposals() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['proposals'], queryFn: () => api.itemProposals() });
  const act = useMutation({
    mutationFn: ({ id, accept }: { id: string; accept: boolean }) =>
      accept ? api.itemAccept(id).then(() => undefined) : api.itemDismiss(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['proposals'] });
      void qc.invalidateQueries({ queryKey: ['items'] });
    },
  });
  const [open, setOpen] = useState(false);
  const rows = q.data ?? [];
  if (rows.length === 0) return null;
  return (
    <div
      style={{
        background: 'var(--nn-panel-2)',
        border: '1px solid var(--nn-hairline)',
        borderRadius: 12,
        padding: 13,
      }}
    >
      <Group justify="space-between" wrap="nowrap">
        <Text fz={13}>
          {rows.length} {rows.length === 1 ? 'phrase' : 'phrases'} I’ve noticed you keep meeting.
        </Text>
        <Button size="compact-xs" variant="subtle" onClick={() => setOpen((o) => !o)}>
          {open ? 'Later' : 'Review'}
        </Button>
      </Group>
      {open && (
        <Stack gap={10} mt={10}>
          {rows.map((r) => (
            <div key={r.item.id}>
              <Text fz={14.5} lang="ka">
                {r.item.displayForm ?? r.item.lemma}
              </Text>
              {r.evidence.slice(0, 2).map((ex) => (
                <Text key={ex.lineId} fz={12} c="dimmed" lang="ka" lineClamp={1}>
                  {ex.text}
                </Text>
              ))}
              <Group gap={8} mt={5}>
                <Button
                  size="compact-xs"
                  variant="default"
                  onClick={() => act.mutate({ id: r.item.id, accept: true })}
                >
                  Track it
                </Button>
                <Button
                  size="compact-xs"
                  variant="subtle"
                  onClick={() => act.mutate({ id: r.item.id, accept: false })}
                >
                  Not now
                </Button>
              </Group>
            </div>
          ))}
        </Stack>
      )}
    </div>
  );
}

/**
 * Pull the audio of every saved word through the service worker, so airplane mode still has a
 * voice. Two sources: the pages the list has actually loaded, and one extra request for up to
 * 200 whose ONLY purpose is warming — most learners never scroll to page four, and page four is
 * exactly what they will want on the metro.
 *
 * Nothing is awaited and every error is swallowed: this is a courtesy running behind the screen,
 * never a step the learner is waiting on. `warmed` stops a re-render costing a second fetch.
 */
function useWarmSavedAudio(items: SavedItem[]) {
  const warmed = useRef<Set<string>>(new Set());
  const warm = useCallback((list: Array<Pick<SavedItem, 'audio'>>) => {
    const urls = list
      .map((i) => i.audio?.url)
      .filter((u): u is string => !!u && !warmed.current.has(u));
    if (urls.length === 0) return;
    for (const u of urls) warmed.current.add(u);
    void warmAudio(urls, fetch);
  }, []);

  useEffect(() => {
    warm(items);
  }, [items, warm]);

  // One page of 200 (`PageQueryFields` caps `limit` there), fetched for its urls and nothing else.
  useEffect(() => {
    let cancelled = false;
    void api
      .savedList({ limit: 200 })
      .then((page) => {
        if (!cancelled) warm(page.items);
      })
      .catch(() => {
        /* offline, or unentitled: the pages already on screen were warmed either way */
      });
    return () => {
      cancelled = true;
    };
  }, [warm]);
}

const SAVED_KEY = ['saved', undefined] as const;

function SavedList() {
  const qc = useQueryClient();
  const online = useOnline();
  const q = useInfiniteQuery({
    queryKey: SAVED_KEY,
    queryFn: ({ pageParam }) => api.savedList({ cursor: pageParam }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const del = useMutation({
    mutationFn: (id: string) => api.savedDelete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['saved'] }),
  });
  const items = q.data?.pages.flatMap((p) => p.items) ?? [];
  useWarmSavedAudio(items);

  if (q.isPending) {
    return (
      <Stack gap="xs">
        <Skeleton height={84} radius={12} />
        <Skeleton height={84} radius={12} />
        <Skeleton height={84} radius={12} />
      </Stack>
    );
  }
  if (q.error) {
    return (
      <Text c="dimmed" fz="sm">
        Couldn’t load your saved words.
      </Text>
    );
  }
  if (items.length === 0) {
    return (
      <div
        style={{
          background: 'var(--nn-panel)',
          border: '1px solid var(--nn-hairline)',
          borderRadius: 12,
          padding: 16,
        }}
      >
        <Text fz={13} c="dimmed" style={{ lineHeight: 1.55 }}>
          Words you save from videos collect here.
        </Text>
        <Link
          to="/watch"
          style={{ display: 'inline-block', marginTop: 8, fontSize: 12, color: 'var(--nn-link)' }}
        >
          Open the library →
        </Link>
      </div>
    );
  }
  return (
    <Stack gap="xs">
      {items.map((it) => (
        <SavedRow
          key={it.id}
          item={it}
          onDelete={() => del.mutate(it.id)}
          deleting={del.isPending && del.variables === it.id}
        />
      ))}
      {del.error && (
        <Text c="dimmed" fz="sm">
          Couldn’t delete that one — try again.
        </Text>
      )}
      {q.hasNextPage &&
        (q.isFetchNextPageError || !online ? (
          // Offline the first page is cached and the rest are not. Say that once, plainly,
          // rather than leaving a button that can only fail.
          <Text c="dimmed" fz="sm">
            These are the ones you have offline — the rest are here when the connection is.
          </Text>
        ) : (
          <Button
            variant="default"
            fullWidth
            onClick={() => q.fetchNextPage()}
            loading={q.isFetchingNextPage}
          >
            Load more
          </Button>
        ))}
    </Stack>
  );
}

/**
 * One saved row (frame 4e): sentence with the selection bolded · translation · ▶ (cached TTS
 * when the save produced audio, else `speak` — works for unentitled learners when audio is
 * cached, rule 10) · source link · note. Delete is two taps everywhere: on touch a left swipe
 * reveals Delete; with a fine pointer a ✕ reveals it; the second tap deletes.
 */
function SavedRow({
  item,
  onDelete,
  deleting,
}: {
  item: SavedItem;
  onDelete: () => void;
  deleting: boolean;
}) {
  const finePointer = useMediaQuery('(hover: hover) and (pointer: fine)', false, {
    getInitialValueInEffect: false,
  });
  const [open, setOpen] = useState(false);
  const [offset, setOffset] = useState(0);
  const [dragging, setDragging] = useState(false);
  const drag = useRef<{ x: number; y: number; axis: SwipeVerdict } | null>(null);
  const [line, setLine] = useState<string | null>(null);

  const myKey = `saved:${item.id}`;
  const status = useTtsKeyStatus(myKey);
  const mine = status !== 'idle';
  const minePlaying = status === 'playing' || status === 'loading';

  const onPlay = async () => {
    setLine(null);
    if (minePlaying) return ttsPlayer.pause();
    if (status === 'paused') return ttsPlayer.resume();
    const track = savedTrack(item);
    try {
      if (track) await ttsPlayer.play(track);
      else
        await speak(item.sentence, {
          title: savedTitle(item),
          subtitle: 'saved line · TTS',
          key: myKey,
        });
    } catch (err) {
      setLine(ttsCalmLine(err));
    }
  };

  const base = open ? -SWIPE_OPEN_PX : 0;
  const settle = (to: 'open' | 'closed') => {
    setOpen(to === 'open');
    setOffset(to === 'open' ? -SWIPE_OPEN_PX : 0);
    setDragging(false);
    drag.current = null;
  };

  const split = splitSelection(item.sentence, item.selection);
  const translation = savedTranslation(item);

  return (
    <div style={{ position: 'relative', borderRadius: 12, overflow: 'hidden' }}>
      {/* behind the card: the revealed Delete (the second tap) */}
      <UnstyledButton
        onClick={onDelete}
        disabled={deleting}
        aria-label="Delete saved item"
        tabIndex={open ? 0 : -1}
        style={{
          position: 'absolute',
          top: 0,
          right: 0,
          bottom: 0,
          width: SWIPE_OPEN_PX,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 12.5,
          fontWeight: 600,
          // theme.other.errorTint, scheme-paired like every other use of this coral
          // (JobPill.tsx:46, LineEditor.tsx:67, ScenesBar.tsx:61) — never the accent.
          color: 'light-dark(#b0433b, #e0938c)',
          background: 'light-dark(rgba(176,67,59,.14), rgba(224,100,92,.18))',
          opacity: open || dragging ? 1 : 0,
        }}
      >
        {deleting ? '…' : 'Delete'}
      </UnstyledButton>
      <div
        onPointerDown={(e) => {
          if (e.pointerType !== 'touch') return;
          drag.current = { x: e.clientX, y: e.clientY, axis: 'none' };
        }}
        onPointerMove={(e) => {
          const d = drag.current;
          if (!d) return;
          const dx = e.clientX - d.x;
          const dy = e.clientY - d.y;
          d.axis = swipeAxis(dx, dy, d.axis);
          if (d.axis === 'horizontal') {
            setDragging(true);
            setOffset(swipeOffset(dx + base));
          }
        }}
        onPointerUp={(e) => {
          const d = drag.current;
          if (!d) return;
          if (d.axis === 'horizontal') settle(swipeSettles(e.clientX - d.x + base));
          else drag.current = null;
        }}
        onPointerCancel={() => settle(open ? 'open' : 'closed')}
        onClick={() => {
          if (open) settle('closed');
        }}
        style={{
          position: 'relative',
          background: 'var(--nn-panel)',
          border: '1px solid var(--nn-hairline)',
          borderRadius: 12,
          padding: '14px 16px',
          transform: `translateX(${offset}px)`,
          transition: dragging ? 'none' : 'transform 160ms ease-out',
          touchAction: 'pan-y',
          userSelect: 'none',
          WebkitUserSelect: 'none',
        }}
      >
        <Group justify="space-between" wrap="nowrap" align="flex-start" gap="xs">
          <div style={{ minWidth: 0, flex: 1 }}>
            <div lang="ka" style={{ fontSize: 15, lineHeight: 1.65 }}>
              {split.before}
              <span style={{ fontWeight: 600, color: 'var(--nn-text-strong)' }}>{split.match}</span>
              {split.after}
            </div>
            {translation && (
              <Text fz={12} c="dimmed" mt={3} lang="en">
                {translation}
              </Text>
            )}
            {item.note && (
              <Text fz={12} c="dimmed" mt={3} fs="italic" lang="en">
                {item.note}
              </Text>
            )}
          </div>
          {finePointer && !open && (
            <UnstyledButton
              onClick={(e) => {
                e.stopPropagation();
                settle('open');
              }}
              aria-label="Delete…"
              style={{
                width: 40,
                height: 40,
                margin: '-10px -12px 0 0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--nn-faint)',
                fontSize: 14,
                flexShrink: 0,
              }}
            >
              ✕
            </UnstyledButton>
          )}
        </Group>
        <Group gap={9} mt={4} wrap="wrap" align="center">
          <UnstyledButton
            onClick={(e) => {
              e.stopPropagation();
              void onPlay();
            }}
            aria-label={minePlaying ? 'Pause' : 'Hear it'}
            style={{
              height: 40,
              display: 'flex',
              alignItems: 'center',
              margin: '0 -4px',
              padding: '0 4px',
            }}
          >
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 5,
                padding: '3px 10px',
                borderRadius: 999,
                border: `1px solid ${mine ? 'var(--nn-accent)' : 'var(--nn-hairline)'}`,
                color: mine ? 'var(--nn-accent-text)' : 'var(--nn-text)',
                fontSize: 11,
                lineHeight: 1.6,
              }}
            >
              <span style={{ display: 'flex', transform: 'scale(.5)', margin: '-6px -8px' }}>
                {minePlaying ? <IconPause /> : <IconPlay />}
              </span>
              TTS
            </span>
          </UnstyledButton>
          {/* M4a: `lineGet` gives the line its seek time, so the link lands ON the sentence
              instead of at the top of a 40-minute upload. A line whose video went away (rule 4)
              404s — then the link is simply the video, or nothing. */}
          {item.videoId && <OpenAtLine videoId={item.videoId} lineId={item.lineId} />}
          {line && (
            <Text fz={11} c="dimmed">
              {line}
            </Text>
          )}
        </Group>
      </div>
    </div>
  );
}

/**
 * "Open video →" that seeks to the saved line. The `lineGet` call is per row and lazy (it only
 * runs when the row is on screen), and its result is cached by react-query, so scrolling the list
 * costs one request per line at most.
 */
function OpenAtLine({ videoId, lineId }: { videoId: string; lineId: string | null }) {
  const q = useQuery({
    queryKey: ['line', lineId],
    queryFn: () => api.lineGet(lineId!),
    enabled: !!lineId,
    retry: false,
    staleTime: 5 * 60_000,
  });
  const t = q.data ? Math.max(0, Math.floor(q.data.startMs / 1000)) : null;
  return (
    <Link
      to={t === null ? `/watch/${videoId}` : `/watch/${videoId}?t=${t}`}
      onClick={(e) => e.stopPropagation()}
      style={{ fontSize: 11, color: 'var(--nn-muted)', textDecoration: 'none' }}
    >
      {t === null ? 'Open video →' : 'Open at this line →'}
    </Link>
  );
}
